import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode
} from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type User,
  type UserCredential
} from 'firebase/auth';
import { getFirebaseAuth, hasFirebaseConfig } from '../config/firebase';

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<UserCredential>;
  register: (email: string, password: string) => Promise<UserCredential>;
  logout: () => Promise<void>;
  /** Pass true after custom claims change (e.g. issuer register) so the next API call sees updated claims. */
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hasFirebaseConfig()) {
      setLoading(false);
      return;
    }
    try {
      const auth = getFirebaseAuth();
      const unsubscribe = onAuthStateChanged(auth, (u) => {
        setUser(u ?? null);
        setLoading(false);
      });
      return () => unsubscribe();
    } catch {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<UserCredential> => {
    const auth = getFirebaseAuth();
    return signInWithEmailAndPassword(auth, email, password);
  }, []);

  const register = useCallback(async (email: string, password: string): Promise<UserCredential> => {
    const auth = getFirebaseAuth();
    return createUserWithEmailAndPassword(auth, email, password);
  }, []);

  const logout = useCallback(async () => {
    const auth = getFirebaseAuth();
    try {
      const { unregisterPassportPushDevice, clearPassportPushOptInLocal } = await import(
        '../services/passportMobilePush'
      );
      const token = await auth.currentUser?.getIdToken(false);
      await unregisterPassportPushDevice(token ?? null);
      clearPassportPushOptInLocal();
    } catch {
      /* ignore */
    }
    await firebaseSignOut(auth);
  }, []);

  const getIdToken = useCallback(async (forceRefresh?: boolean): Promise<string | null> => {
    if (!user) return null;
    return user.getIdToken(!!forceRefresh);
  }, [user]);

  const value: AuthContextValue = {
    user,
    loading,
    login,
    register,
    logout,
    getIdToken
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
